import { useState, useEffect, useRef } from 'react';
import { Plus, Save, Trash2, Square, Circle as CircleIcon, Minus, Type, MousePointer, Monitor, Edit2, Eye, EyeOff } from 'lucide-react';
import { floorPlanApi, machineApi } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import { Card, Button, Modal } from '@/components/common';
import { FloorPlanCanvas } from '@/components/floorplan';
import type { GymFloorPlan, FloorPlanDrawingData, Machine } from '@/types';

type Tool = 'select' | 'rect' | 'circle' | 'line' | 'text' | 'delete';

const FloorPlan = () => {
  const [floorPlans, setFloorPlans] = useState<GymFloorPlan[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<GymFloorPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [renamePlanName, setRenamePlanName] = useState('');
  const [selectedTool, setSelectedTool] = useState<Tool>('select');
  const [hasChanges, setHasChanges] = useState(false);
  const [showMachines, setShowMachines] = useState(true);
  const { showToast } = useUIStore();
  
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    loadFloorPlans();
    loadMachines();
  }, []);

  // Responsive canvas size
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasContainerRef.current) {
        const containerWidth = canvasContainerRef.current.clientWidth - 32; // padding
        const width = Math.min(containerWidth, 1200);
        const height = Math.round(width * 0.75); // 4:3 aspect ratio
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [selectedPlan]);

  const loadFloorPlans = async () => {
    try {
      const response = await floorPlanApi.getAll();
      if (response.success && response.data) {
        setFloorPlans(response.data.floorPlans);
        if (response.data.floorPlans.length > 0) {
          setSelectedPlan(response.data.floorPlans[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load floor plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMachines = async () => {
    try {
      const response = await machineApi.getAll({ includeAll: true });
      if (response.success && response.data) {
        setMachines(response.data.machines);
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlanName.trim()) return;

    try {
      const response = await floorPlanApi.create({
        name: newPlanName,
        drawingData: {
          version: 1,
          width: canvasSize.width,
          height: canvasSize.height,
          shapes: [],
        },
      });

      if (response.success && response.data) {
        setFloorPlans([...floorPlans, response.data.floorPlan]);
        setSelectedPlan(response.data.floorPlan);
        setShowNewModal(false);
        setNewPlanName('');
        showToast('Alaprajz létrehozva!', 'success');
      }
    } catch (error) {
      console.error('Failed to create floor plan:', error);
      showToast('Hiba az alaprajz létrehozásakor.', 'error');
    }
  };

  const handleDrawingChange = (drawingData: FloorPlanDrawingData) => {
    if (!selectedPlan) return;
    
    setSelectedPlan({
      ...selectedPlan,
      drawingData,
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedPlan) return;
    
    setSaving(true);
    try {
      const response = await floorPlanApi.update(selectedPlan.id, {
        drawingData: selectedPlan.drawingData,
      });

      if (response.success) {
        setHasChanges(false);
        showToast('Alaprajz mentve!', 'success');
        
        // Update in list
        setFloorPlans(floorPlans.map(fp => 
          fp.id === selectedPlan.id ? selectedPlan : fp
        ));
      }
    } catch (error) {
      console.error('Failed to save floor plan:', error);
      showToast('Hiba az alaprajz mentésekor.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan) return;
    if (!confirm(`Biztosan törlöd a "${selectedPlan.name}" alaprajzot?`)) return;

    try {
      await floorPlanApi.delete(selectedPlan.id);
      const updatedPlans = floorPlans.filter(fp => fp.id !== selectedPlan.id);
      setFloorPlans(updatedPlans);
      setSelectedPlan(updatedPlans.length > 0 ? updatedPlans[0] : null);
      setHasChanges(false);
      showToast('Alaprajz törölve!', 'success');
    } catch (error) {
      console.error('Failed to delete floor plan:', error);
      showToast('Hiba az alaprajz törlésekor.', 'error');
    }
  };

  const openRenameModal = () => {
    if (!selectedPlan) return;
    setRenamePlanName(selectedPlan.name);
    setShowRenameModal(true);
  };

  const handleRenamePlan = async () => {
    if (!selectedPlan || !renamePlanName.trim()) return;

    try {
      const response = await floorPlanApi.update(selectedPlan.id, {
        name: renamePlanName.trim(),
      });

      if (response.success && response.data) {
        const updatedPlan = { ...selectedPlan, name: renamePlanName.trim() };
        setSelectedPlan(updatedPlan);
        setFloorPlans(floorPlans.map(fp => 
          fp.id === selectedPlan.id ? updatedPlan : fp
        ));
        setShowRenameModal(false);
        showToast('Alaprajz átnevezve!', 'success');
      }
    } catch (error) {
      console.error('Failed to rename floor plan:', error);
      showToast('Hiba az alaprajz átnevezésekor.', 'error');
    }
  };

  const handleToggleActive = async (plan: GymFloorPlan) => {
    const newActive = !plan.isActive;
    try {
      const response = await floorPlanApi.update(plan.id, { isActive: newActive });
      if (response.success && response.data) {
        const updatedPlan = { ...plan, isActive: newActive };
        setFloorPlans(floorPlans.map(fp => fp.id === plan.id ? updatedPlan : fp));
        if (selectedPlan?.id === plan.id) {
          setSelectedPlan(updatedPlan);
        }
        showToast(
          newActive ? `"${plan.name}" aktiválva – megjelenik edzés közben.` : `"${plan.name}" inaktív – nem jelenik meg edzés közben.`,
          'success'
        );
      }
    } catch (error) {
      console.error('Failed to toggle floor plan status:', error);
      showToast('Hiba az állapot módosításakor.', 'error');
    }
  };

  // Get machines for the selected floor plan
  const getMachinesOnPlan = () => {
    if (!selectedPlan) return [];
    return machines.filter(m => 
      m.floorPlanPosition?.floorPlanId === selectedPlan.id
    );
  };

  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: 'select', icon: <MousePointer className="w-4 h-4" />, label: 'Kijelölés' },
    { id: 'rect', icon: <Square className="w-4 h-4" />, label: 'Téglalap' },
    { id: 'circle', icon: <CircleIcon className="w-4 h-4" />, label: 'Kör' },
    { id: 'line', icon: <Minus className="w-4 h-4" />, label: 'Vonal' },
    { id: 'text', icon: <Type className="w-4 h-4" />, label: 'Szöveg' },
    { id: 'delete', icon: <Trash2 className="w-4 h-4" />, label: 'Törlés' },
  ];

  const machinesOnPlan = getMachinesOnPlan();

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Terem alaprajz</h1>
          <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
            Rajzold meg a konditerem alaprajzát és jelöld be a gépek helyét
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap">
          <Button
            variant="outline"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowNewModal(true)}
            className="whitespace-nowrap"
          >
            Új alaprajz
          </Button>
          {selectedPlan && (
            <Button
              size="sm"
              icon={<Save className="w-4 h-4" />}
              loading={saving}
              onClick={handleSave}
              disabled={!hasChanges}
              className="whitespace-nowrap"
            >
              Mentés
            </Button>
          )}
        </div>
      </div>

      {floorPlans.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nincs még alaprajz</h3>
            <p className="text-gray-600 mb-4">
              Hozz létre egy új alaprajzot a konditerem térképezéséhez.
            </p>
            <Button
              icon={<Plus className="w-5 h-5" />}
              onClick={() => setShowNewModal(true)}
            >
              Új alaprajz létrehozása
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* Floor Plans List */}
            <Card title="Alaprajzok">
              <p className="text-xs text-gray-500 mb-3">
                Inaktív alaprajzok nem jelennek meg edzés közben (gyakorlat hozzáadás, csere).
              </p>
              <div className="space-y-2">
                {floorPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                      selectedPlan?.id === plan.id
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : plan.isActive
                          ? 'hover:bg-gray-100 text-gray-700'
                          : 'hover:bg-gray-100 text-gray-400 bg-gray-50'
                    }`}
                  >
                    <button
                      className="flex-1 text-left min-w-0"
                      onClick={() => {
                        if (hasChanges) {
                          if (confirm('Mentetlen változtatásaid vannak. Biztosan váltasz?')) {
                            setSelectedPlan(plan);
                            setHasChanges(false);
                          }
                        } else {
                          setSelectedPlan(plan);
                        }
                      }}
                    >
                      <span className={plan.isActive ? '' : 'line-through opacity-70'}>{plan.name}</span>
                      {!plan.isActive && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-normal">inaktív</span>
                      )}
                    </button>
                    <button
                      onClick={() => handleToggleActive(plan)}
                      className={`p-1 rounded flex-shrink-0 ${
                        plan.isActive
                          ? 'text-green-600 hover:bg-green-100'
                          : 'text-gray-400 hover:bg-gray-200'
                      }`}
                      title={plan.isActive ? 'Inaktívvá tétel' : 'Aktívvá tétel'}
                    >
                      {plan.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    {selectedPlan?.id === plan.id && (
                      <button
                        onClick={openRenameModal}
                        className="p-1 text-primary-600 hover:bg-primary-200 rounded flex-shrink-0"
                        title="Átnevezés"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Tools */}
            <Card title="Eszközök">
              <div className="space-y-1">
                {tools.map((tool) => (
                  <button
                    key={tool.id}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      selectedTool === tool.id
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : tool.id === 'delete'
                        ? 'text-red-600 hover:bg-red-50'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setSelectedTool(tool.id)}
                  >
                    {tool.icon}
                    <span>{tool.label}</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Machines on this plan */}
            {selectedPlan && (
              <Card 
                title="Gépek ezen az alaprajzon"
                headerActions={
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={showMachines}
                      onChange={(e) => setShowMachines(e.target.checked)}
                      className="rounded"
                    />
                    Mutasd
                  </label>
                }
              >
                {machinesOnPlan.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Nincs gép ezen az alaprajzon. A Gépek menüben adhatsz hozzá.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {machinesOnPlan.map((machine) => (
                      <div
                        key={machine.id}
                        className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded text-sm"
                      >
                        <Monitor className="w-4 h-4 text-primary-600" />
                        <span className="truncate">{machine.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Delete Plan */}
            {selectedPlan && (
              <Card>
                <button
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  onClick={handleDeletePlan}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Alaprajz törlése</span>
                </button>
              </Card>
            )}
          </div>

          {/* Canvas Area */}
          <div className="lg:col-span-3" ref={canvasContainerRef}>
            <Card>
              {selectedPlan && (
                <div className="relative">
                  <FloorPlanCanvas
                    drawingData={selectedPlan.drawingData}
                    onChange={handleDrawingChange}
                    selectedTool={selectedTool}
                    width={canvasSize.width}
                    height={canvasSize.height}
                  />
                  
                  {/* Machine markers overlay */}
                  {showMachines && machinesOnPlan.length > 0 && (
                    <div 
                      className="absolute top-0 left-0 pointer-events-none"
                      style={{ width: canvasSize.width, height: canvasSize.height }}
                    >
                      {machinesOnPlan.map((machine) => {
                        if (!machine.floorPlanPosition) return null;
                        
                        // Scale position to current canvas size
                        const scaleX = canvasSize.width / selectedPlan.drawingData.width;
                        const scaleY = canvasSize.height / selectedPlan.drawingData.height;
                        const x = machine.floorPlanPosition.x * scaleX;
                        const y = machine.floorPlanPosition.y * scaleY;

                        return (
                          <div
                            key={machine.id}
                            className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
                            style={{ left: x, top: y }}
                            title={machine.name}
                          >
                            <div className="relative group">
                              {/* Marker */}
                              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                                <Monitor className="w-4 h-4 text-white" />
                              </div>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                  {machine.name}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <p>
                  <strong>Tipp:</strong> Válassz egy eszközt és rajzolj a vászonra. 
                  Kijelölés módban húzhatod és átméretezheted az elemeket.
                </p>
                {hasChanges && (
                  <span className="text-orange-600 font-medium">
                    * Mentetlen változtatások
                  </span>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* New Plan Modal */}
      <Modal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        title="Új alaprajz létrehozása"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowNewModal(false)}
            >
              Mégse
            </button>
            <Button onClick={handleCreatePlan}>
              Létrehozás
            </Button>
          </>
        }
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alaprajz neve
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="pl. Főterem, Kardió zóna"
            value={newPlanName}
            onChange={(e) => setNewPlanName(e.target.value)}
            autoFocus
          />
        </div>
      </Modal>

      {/* Rename Plan Modal */}
      <Modal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Alaprajz átnevezése"
        footer={
          <>
            <button
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              onClick={() => setShowRenameModal(false)}
            >
              Mégse
            </button>
            <Button onClick={handleRenamePlan}>
              Mentés
            </Button>
          </>
        }
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alaprajz neve
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="pl. Főterem, Kardió zóna"
            value={renamePlanName}
            onChange={(e) => setRenamePlanName(e.target.value)}
            autoFocus
          />
        </div>
      </Modal>
    </div>
  );
};

export default FloorPlan;
